import React, { useRef } from 'react'
import PropTypes from 'prop-types'
import { ContentFallback, Paginator, Button, ErrorBoundary, Heading, Link } from '@deseretbook/react-ui'
import { Review } from './Review'
import { usePagination } from './usePagination'

/**
 * This is a container for Review Components
 * @param {integer} total - The length of the reviews array
 * @param {string} filters - Current filters
 * @param {fun} setFilters - Function to setFilters
 * @param {array} reviews - an array of Review objects
 * @param { integer} productId - Used for write a review button if no reviews are present
 * @returns {func} Container Component
 */
export const ReviewsList = ({
  total = 0,
  filters,
  setFilters,
  reviews = [],
  productId
}) => {
  const {
    currentPage,
    setCurrentPage,
    totalPages,
    minIndex,
    maxIndex,
    noItems
  } = usePagination(total, reviews.length, 5)
  const scrollElement = useRef(null)
  const { keywords, rating } = filters

  // Builds a error message if current filters return 0 reviews
  const filterErrorMessage = `No reviews match 
    ${keywords && `"${keywords}"`}
    ${(keywords && rating && rating !== 10) ? ' with ' : ''}
    ${(rating && rating !== 10) ? `a ${rating} star rating` : ''}`

  // This button is used when there are no reviews for the product
  const writeReviewButton = <Button href={`/products/${productId}/reviews/new`} color="action" asComponent={Link}>Write a Review</Button>
  // This button is used when the current filters return 0 reviews
  const clearFilterButton = <Button type="button" color="action" onClick={() => setFilters({ keywords: '', rating: ''})}>Clear Filters</Button>

  const filteredReviews = reviews && reviews.slice(minIndex - 1, maxIndex)

  /**
   * Sets the new current page and moves the user back
   * to the top of the reviews list.
   * @param {integer} newPage - the new page number to load
   */
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage)
    scrollElement.current.scrollIntoView({ block: 'center' })
  }

  return (
    <ContentFallback
      title="No Reviews Here"
      subtitle={noItems
        ? 'Be the first to write one!'
        : filterErrorMessage}
      showFallback={!reviews.length}
      actionContent={noItems
        ? writeReviewButton
        : clearFilterButton
      }
      imageProps={{
        src: 'https://deseretbook.s3.amazonaws.com/images/content_fallback/feedback.svg',
        alt: 'Feedback',
        alignment: 'center',
        imageWidth: '1048px',
        imageHeight: '786px',
        maxWidth: 30,
      }}
    >
      <ErrorBoundary title="Unable to load more reviews, please refresh the page.">
        <Heading>
          Showing {minIndex !== maxIndex
            ? `${minIndex} - ${maxIndex}`
            : minIndex} of {reviews.length} reviews
        </Heading>
        <div ref={scrollElement} />
        {(
          filteredReviews.map(review => <Review key={review.id} keywords={keywords} {...review} />)
        )}
      </ErrorBoundary>
      <Paginator
        currentPage={currentPage}
        totalPages={totalPages}
        setCurrentPage={handlePageChange}
      />
    </ContentFallback>
  )
}

ReviewsList.propTypes = {
  total: PropTypes.number,
  filters: PropTypes.shape({
    keywords: PropTypes.string,
    rating: PropTypes.oneOfType(PropTypes.string, PropTypes.number),
  }),
  setFilters: PropTypes.func.isRequired,
  reviews: PropTypes.arrayOf(PropTypes.object),
  productId: PropTypes.number.isRequired,
}
